/**
 * The data pass (step 99, "Protecting the data") — earned once a session, INVISIBLY: nobody sees,
 * ticks or solves anything (the user's rule). It tells the API this is a real browser or the genuine
 * MyRave app, which lets a signed-out reader open more raves before it is slowed
 * (`backend/src/modules/trust/`).
 *
 * - **In the phone app**: the shell attests (App Attest on iPhone, Play Integrity on Android) to the
 *   server's one-time challenge — the highest trust. A shell from before step 99, or a phone that
 *   cannot, falls back to the web's way.
 * - **On the web**: Cloudflare Turnstile in an off-screen widget the dashboard sets to INVISIBLE,
 *   rendered `interaction-only` — never a checkbox.
 *
 * The pass rides on every API request as `X-MyRave-Pass`. No pass is never a refusal — the reader is
 * only trusted less — so nothing waits on it for more than `WAIT_MS`.
 */
import { apiUrl } from './tenant';
import { inNativeApp, attestApp } from './nativeBridge';
import { readSessionJson, writeSessionJson } from './storage';

const KEY = 'data-pass';
const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
/** How long a request that wants the pass waits for one being earned. */
const WAIT_MS = 1_500;
/** How long earning one may take, all in. */
const EARN_MS = 10_000;

interface Kept { pass: string; expiresAt: number }
interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  remove(id: string): void;
}

let kept: Kept | null = null;
let earning: Promise<Kept | null> | null = null;

function current(): Kept | null {
  kept ??= readSessionJson<Kept | null>(null, KEY, null);
  // A minute short of its end: the server would refuse it on the way.
  return kept && kept.expiresAt - 60_000 > Date.now() ? kept : null;
}

/** The header for a request — the pass this tab holds, if any. */
export function passHeader(): Record<string, string> {
  const p = current();
  return p ? { 'x-myrave-pass': p.pass } : {};
}

/** The header, after waiting at most `WAIT_MS` for a pass being earned (never longer — no pass is fine). */
export async function passHeaderSoon(): Promise<Record<string, string>> {
  if (current() || !earning) return passHeader();
  await Promise.race([earning, new Promise((r) => setTimeout(r, WAIT_MS))]);
  return passHeader();
}

/** Earn a pass for this tab, once (a second call joins the first). Never throws. */
export function earnDataPass(): Promise<Kept | null> {
  if (current()) return Promise.resolve(current());
  earning ??= Promise.race([earn(), new Promise<null>((r) => setTimeout(() => r(null), EARN_MS))])
    .catch(() => null)
    .then((k) => {
      earning = null;
      if (k) { kept = k; writeSessionJson(null, KEY, k); }
      return k;
    });
  return earning;
}

async function earn(): Promise<Kept | null> {
  if (inNativeApp()) {
    const fromApp = await earnInApp().catch(() => null);
    if (fromApp) return fromApp;
  }
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  if (!siteKey) return null;
  const token = await turnstileToken(siteKey);
  return token ? ask({ kind: 'web', token }) : null;
}

async function earnInApp(): Promise<Kept | null> {
  const res = await fetch(`${apiUrl()}/trust/challenge`, { credentials: 'omit' });
  if (!res.ok) return null;
  const { challenge } = (await res.json()) as { challenge: string };
  const answer = await attestApp(challenge, import.meta.env.VITE_GOOGLE_CLOUD_PROJECT_NUMBER as string | undefined);
  if (!answer) return null;
  return ask(answer.platform === 'ios'
    ? { kind: 'ios', keyId: answer.keyId, attestation: answer.attestation, challenge }
    : { kind: 'android', token: answer.token, challenge });
}

async function ask(body: Record<string, string>): Promise<Kept | null> {
  const res = await fetch(`${apiUrl()}/trust/pass`, {
    method: 'POST', credentials: 'omit', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) return null;
  const out = (await res.json()) as { pass: string | null; expiresAt?: number };
  return out.pass && out.expiresAt ? { pass: out.pass, expiresAt: out.expiresAt } : null;
}

/** One invisible Turnstile token: the script loaded on demand, an off-screen widget, removed after. */
async function turnstileToken(siteKey: string): Promise<string | null> {
  const api = await loadTurnstile();
  if (!api) return null;
  const box = document.createElement('div');
  // Off screen and inert: an invisible widget draws nothing, and nothing here may ever be seen.
  box.setAttribute('aria-hidden', 'true');
  box.style.cssText = 'position:fixed;left:-10000px;top:0;width:1px;height:1px;overflow:hidden';
  document.body.append(box);
  try {
    return await new Promise<string | null>((resolve) => {
      let id = '';
      try {
        id = api.render(box, {
          sitekey: siteKey,
          appearance: 'interaction-only',
          callback: (token: string) => resolve(token),
          'error-callback': () => { resolve(null); return true; },
          'unsupported-callback': () => resolve(null),
          'timeout-callback': () => resolve(null),
        });
      } catch { resolve(null); }
      setTimeout(() => { if (id) try { api.remove(id); } catch { /* gone already */ } }, EARN_MS);
    });
  } finally {
    setTimeout(() => box.remove(), EARN_MS);
  }
}

let loading: Promise<TurnstileApi | null> | null = null;
function loadTurnstile(): Promise<TurnstileApi | null> {
  const w = window as unknown as { turnstile?: TurnstileApi };
  if (w.turnstile) return Promise.resolve(w.turnstile);
  loading ??= new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => resolve(w.turnstile ?? null);
    s.onerror = () => { loading = null; resolve(null); };
    document.head.append(s);
  });
  return loading;
}
